import { useCallback, useEffect, useRef, useState } from 'react';

import { useAtom } from 'jotai';

import {
  type AudioDevice,
  availableAudioDevicesAtom,
  selectedAudioDeviceIdAtom,
} from '../../atoms';

export const useAudioDeviceManager = () => {
  const [availableDevices, setAvailableDevices] = useAtom(
    availableAudioDevicesAtom,
  );
  const [selectedDeviceId, setSelectedDeviceId] = useAtom(
    selectedAudioDeviceIdAtom,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedDeviceIdRef = useRef(selectedDeviceId);

  // Keep ref in sync with the atom value
  useEffect(() => {
    selectedDeviceIdRef.current = selectedDeviceId;
  }, [selectedDeviceId]);

  const getAudioDevices = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Request permission with generic audio constraint (no specific device)
      // This avoids the browser showing device selection in the permission prompt
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Get all devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter for audio input devices
      const audioDevices: AudioDevice[] = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
          kind: device.kind,
        }));

      setAvailableDevices(audioDevices);

      // Auto-select first device if none selected or if selected device no longer exists
      if (
        !selectedDeviceId ||
        !audioDevices.some((device) => device.deviceId === selectedDeviceId)
      ) {
        if (audioDevices.length > 0) {
          setSelectedDeviceId(audioDevices[0].deviceId);
        }
      }
    } catch (err) {
      console.error('Failed to get audio devices:', err);
      setError(
        'Failed to access microphone devices. Please check permissions.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedDeviceId, setAvailableDevices, setSelectedDeviceId]);

  // Load devices on mount (only if permission already granted)
  useEffect(() => {
    // Check if we already have permission by trying to enumerate devices
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const audioDevices = devices
          .filter((device) => device.kind === 'audioinput')
          .map((device) => ({
            deviceId: device.deviceId,
            label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
            kind: device.kind,
          }));

        if (audioDevices.length > 0) {
          setAvailableDevices(audioDevices);

          // Only set the first device if no device is currently selected
          // or if the currently selected device is no longer available
          const currentSelectedId = selectedDeviceIdRef.current;
          if (
            !currentSelectedId ||
            !audioDevices.some(
              (device) => device.deviceId === currentSelectedId,
            )
          ) {
            setSelectedDeviceId(audioDevices[0].deviceId);
          }
        }
      })
      .catch(() => {
        // No permission yet, devices will be loaded when permission is granted
      });
  }, [setAvailableDevices, setSelectedDeviceId]);

  // Listen for device changes
  useEffect(() => {
    const handleDeviceChange = () => {
      getAudioDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
    };
  }, [getAudioDevices]);

  // Handle device disconnection
  useEffect(() => {
    if (selectedDeviceId && availableDevices.length > 0) {
      const hasDevice = availableDevices.some(
        (device) => device.deviceId === selectedDeviceId,
      );
      if (!hasDevice) {
        // Selected device no longer exists, auto-select first available
        setSelectedDeviceId(availableDevices[0].deviceId);
      }
    }
  }, [availableDevices, selectedDeviceId, setSelectedDeviceId]);

  return {
    availableDevices,
    selectedDeviceId,
    isLoading,
    error,
    refreshDevices: getAudioDevices,
    selectDevice: setSelectedDeviceId,
  };
};
